import {
    Button, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography
} from "@mui/material";
import moment from "moment";
import { useAsync2 } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../api/API";
import { ApiToken } from "../../api/ApiTokens";
import { ConfirmDialog } from "../../misc/ConfirmDialog";
import { useSnackAction } from "../../misc/SnackAction";
import { ApiTokenCreated } from "./ApiTokenCreated";
import { ApiTokenEditor } from "./ApiTokenEditor";

export function ApiTokenListPage() {
    const [creating, setCreating] = useState(false);

    // the token string only exists in the response that made it, so it's held here
    // until the dialog showing it is dismissed
    const [created, setCreated] = useState<ApiToken | null>(null);
    const [revoking, setRevoking] = useState<ApiToken | null>(null);

    const list = useAsync2(input => api.apiTokens.list(input), {}, []);

    const revoke = useSnackAction(async (input: { id: number }) => {
        await api.apiTokens.upsert({ id: input.id, archived: true });
        setRevoking(null);
        list.reload();
    }, { success: "Revoked" });

    return (
        <>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Typography variant="h5" style={{ flex: 1 }}>API tokens</Typography>
                <Button variant="contained" onClick={() => setCreating(true)}>New token</Button>
            </div>

            <div style={{ height: 8 }} />
            <Typography variant="body2" color="text.secondary">
                A token lets a client application reach this system as you, sent as
                "Authorization: Bearer ...". Tokens are yours alone: nobody else can list them.
            </Typography>

            <div style={{ height: 16 }} />
            {list.LoadingOrErrorElement}

            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Token</TableCell>
                            <TableCell>Expires</TableCell>
                            <TableCell>Last used</TableCell>
                            <TableCell padding="checkbox" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {list.asList.length === 0 && !list.loading && (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography variant="body2" color="text.secondary">
                                        No tokens yet.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {list.asList.map(token => (
                            <TokenRow key={token.id} token={token} onRevoke={() => setRevoking(token)} />
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {creating && (
                <ApiTokenEditor
                    onClose={() => setCreating(false)}
                    onCreated={token => {
                        setCreating(false);
                        setCreated(token);
                        list.reload();
                    }}
                />
            )}

            {created && <ApiTokenCreated value={created} onClose={() => setCreated(null)} />}

            <ConfirmDialog
                open={!!revoking}
                title="Revoke token"
                message={"Anything using \"" + (revoking?.name || "") + "\" stops working on its next request."}
                confirmLabel="Revoke"
                confirmColor="error"
                onConfirm={() => revoke.callback({ id: revoking?.id || 0 })}
                onCancel={() => setRevoking(null)}
            />
        </>
    );
}

function TokenRow(props: { token: ApiToken; onRevoke: () => void }) {
    const token = props.token;
    const expired = moment(token.expiresAt).isBefore(moment());

    return (
        <TableRow>
            <TableCell>{token.name}</TableCell>
            <TableCell sx={{ fontFamily: "monospace" }}>{"tsk_" + token.prefix + "_…"}</TableCell>
            <TableCell>
                {moment(token.expiresAt).format("MMM D, YYYY")}
                {expired && <Chip size="small" color="error" label="Expired" sx={{ ml: 1 }} />}
            </TableCell>
            <TableCell>
                {token.lastUsedAt ? moment(token.lastUsedAt).fromNow() : "Never"}
            </TableCell>
            <TableCell padding="checkbox">
                <Button size="small" color="error" onClick={props.onRevoke}>Revoke</Button>
            </TableCell>
        </TableRow>
    );
}
